import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subscription, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import ListViewColumn from '../../../model/list-view-column';
import { NavigationService } from '../../../service/navigation.service';
import { ListPreferences, UserPreferencesService } from '../../../service/user-preferences.service';
import { ColumnDefinition } from '../../../../core/model/column-definition';

function equalColumnSequence(columns: ListViewColumn[], preferences: number[]) {
  if (columns.length !== preferences.length) {
    return false;
  }

  let isEqual = true;
  columns.forEach((col1, index) => {
    const col2 = preferences[index];
    if (col1.id !== col2) isEqual = false;
  });

  return isEqual;
}

function getColumnSignature(columns: ListViewColumn[]): string {
  return columns
    .map(column => column.binding)
    .slice()
    .sort()
    .join(',');
}

@Component({
    selector: 'app-list-view-toolbar',
    templateUrl: './list-view-toolbar.component.html',
    styleUrls: ['./list-view-toolbar.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ListViewToolbarComponent implements OnDestroy, OnChanges {
  constructor(
    private readonly _navigation: NavigationService,
    private readonly _preferencesStorage: UserPreferencesService
  ) {}

  @Input() columnDefinition: ColumnDefinition[];
  @Input() columns: ListViewColumn[];
  @Input() preferencesKey: string;
  @Input() isSticky: boolean = false;
  @Output() columnsChanged = new EventEmitter<ListViewColumn[]>();

  private _id: number;
  private _preferences: ListPreferences;
  private _initializedKey: string;
  private _initializingKey: string | null;
  private readonly _subscription = new Subscription();
  isChangeColumnsSidebarOpen: boolean = false;

  private _hasInputsForId(): boolean {
    return !!(this.preferencesKey && this.columns?.length);
  }

  private _canInitPreferences(): boolean {
    return !!(this.columns?.length && this.preferencesKey);
  }

  private _hashToNumber(value: string): number {
    // Simple deterministic 32-bit hash to keep the id numeric
    let hash = 0;
    for (const ch of value) {
      hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
    }
    return hash;
  }

  private _getId(): number {
    const url = this._navigation.url.replace(/[\w\d]{8}(-[\w\d]{4}){3}-[\w\d]{12}/, '');
    return this._hashToNumber(`${this.preferencesKey}${url}`);
  }

  private _getLegacyId(): number {
    let defSum = 0;
    for (let column of this.columns) {
      column.binding
        .split('')
        .map(ch => ch.charCodeAt(0))
        .forEach(x => (defSum += x));
    }
    let urlSum = 0;
    let url = this._navigation.url.replace(/[\w\d]{8}(-[\w\d]{4}){3}-[\w\d]{12}/, '');
    for (let c of url) {
      urlSum += c.charCodeAt(0);
    }
    return parseInt(`${defSum}${urlSum}`);
  }

  editClick(sidebarColumns: any): void {
    this.columns.sort((a, b) => {
      const indexA = sidebarColumns.indexOf(sidebarColumns.find(x => x.id === a.id));
      const indexB = sidebarColumns.indexOf(sidebarColumns.find(x => x.id === b.id));
      return indexA - indexB;
    });

    sidebarColumns.forEach(col => {
      this.columns.find(x => x.id === col.id).selected = col.selected;
    });

    const selected = this.columns.filter(x => x.selected);
    let updatePref = !equalColumnSequence(selected, this._preferences.columns);
    if (updatePref) {
      const pref = { id: this._id, columns: selected.map(x => x.id), orderBy: 0 };
      this._subscription.add(this._preferencesStorage.setListPreferences(pref).subscribe(() => (this._preferences = pref)));
    }
    this.columnsChanged.emit(this.columns);
  }

  ngOnDestroy() {
    this._subscription.unsubscribe();
  }

  openSideBar(): void {
    this.isChangeColumnsSidebarOpen = true;
  }

  sideBarCancel(): void {
    this.isChangeColumnsSidebarOpen = false;
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes['columns'] || changes['columnDefinition'] || changes['preferencesKey']) {
      this._tryInitPreferences();
    }
  }

  private _tryInitPreferences(): void {
    if (!this._hasInputsForId() || !this._canInitPreferences()) {
      return;
    }

    const id = this._getId();
    const initKey = `${id}:${getColumnSignature(this.columns)}`;
    if (initKey === this._initializedKey || initKey === this._initializingKey) {
      return;
    }

    this._id = id;
    this._initializingKey = initKey;
    this._initPreferences(initKey);
  }

  private _initPreferences(initKey: string): void {
    this._subscription.add(
      this._preferencesStorage
        .getListPreferences(this._id)
        .pipe(
          switchMap(pref => {
            const existing = pref ?? { id: this._id, columns: [], orderBy: 0 };
            // keep only IDs that exist in current columns
            existing.columns = (existing.columns ?? []).filter(id => this.columns.some(c => c.id === id));

            if (!existing.columns.length) {
              // Try to migrate from legacy ID once
              const legacyId = this._getLegacyId();
              return this._preferencesStorage.getListPreferences(legacyId).pipe(
                switchMap(legacy => {
                  if (legacy) {
                    const migratedColumns = (legacy.columns ?? []).filter(id => this.columns.some(c => c.id === id));
                    if (migratedColumns.length) {
                      const migrated = { id: this._id, columns: migratedColumns, orderBy: legacy.orderBy ?? 0 };
                      return this._preferencesStorage.setListPreferences(migrated).pipe(map(() => migrated));
                    }
                  }
                  // No legacy found or nothing valid to migrate → seed defaults
                  const defaultIds = this.columns.filter(x => x.selected).map(x => x.id);
                  const nextPref = { ...existing, columns: defaultIds };
                  return this._preferencesStorage.setListPreferences(nextPref).pipe(map(() => nextPref));
                })
              );
            }

            return of({ ...existing });
          })
        )
        .subscribe({
          next: pref => {
            this._preferences = pref;
            this.columns.sort((a, b) => {
              const indexA = this._preferences.columns.indexOf(a.id);
              const indexB = this._preferences.columns.indexOf(b.id);
              return indexA - indexB;
            });

            this.columns.forEach(col => (col.selected = this._preferences.columns.includes(col.id)));
            this._initializedKey = initKey;
            this._initializingKey = null;
            this.columnsChanged.emit(this.columns);
          },
          error: () => {
            this._initializingKey = null;
          },
        })
    );
  }
}
